import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { X, FileText, Upload } from "lucide-react";

const PLATFORMS = ["Instagram", "LinkedIn", "Facebook", "Twitter/X", "Other"];
const EMPTY = { platform: "Instagram", title: "", content: "", hashtags: "", post_date: "" };

export default function SocialMediaPostFormDialog({ open, onOpenChange, post, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [existingMedia, setExistingMedia] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!post;

  useEffect(() => {
    if (open) {
      setForm(
        post
          ? { platform: post.platform, title: post.title, content: post.content || "", hashtags: (post.hashtags || []).join(", "), post_date: post.post_date || "" }
          : EMPTY
      );
      setExistingMedia(post?.media || []);
      setNewFiles([]);
    }
  }, [open, post]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addFiles = (fileList) => {
    setNewFiles((prev) => [...prev, ...Array.from(fileList)]);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("platform", form.platform);
      fd.append("title", form.title.trim());
      fd.append("content", form.content || "");
      fd.append("hashtags", form.hashtags || "");
      fd.append("post_date", form.post_date || "");
      newFiles.forEach((f) => fd.append("files", f));
      if (isEdit) {
        fd.append("keep_media", JSON.stringify(existingMedia.map((m) => m.file_path)));
        await api.put(`/social-media/${post.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Post updated");
      } else {
        await api.post("/social-media", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Post added to records");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="social-post-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Post" : "Add Social Media Post"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Platform</Label>
              <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                <SelectTrigger data-testid="social-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Post Date</Label>
              <Input data-testid="social-post-date-input" type="date" value={form.post_date} onChange={set("post_date")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input data-testid="social-title-input" value={form.title} onChange={set("title")} placeholder="e.g. Earth Day Campaign Launch" />
          </div>
          <div className="space-y-1.5">
            <Label>Content / Caption</Label>
            <Textarea data-testid="social-content-input" value={form.content} onChange={set("content")} rows={4} placeholder="Write the post caption..." />
          </div>
          <div className="space-y-1.5">
            <Label>Hashtags (comma-separated)</Label>
            <Input data-testid="social-hashtags-input" value={form.hashtags} onChange={set("hashtags")} placeholder="sustainability, esg, envirowealth" />
          </div>
          <div className="space-y-1.5">
            <Label>Images / PDF</Label>
            <label className="flex items-center justify-center gap-2 border border-dashed border-[#E2E8F0] rounded-md py-3 cursor-pointer text-sm text-[#4B5563] hover:bg-[#F8F9FA] transition-colors duration-200">
              <Upload className="h-4 w-4" /> Add files
              <input type="file" multiple accept="image/*,application/pdf" className="hidden" data-testid="social-files-input" onChange={(e) => e.target.files.length && addFiles(e.target.files)} />
            </label>
            {(existingMedia.length > 0 || newFiles.length > 0) && (
              <div className="grid grid-cols-4 gap-2 mt-2">
                {existingMedia.map((m) => (
                  <MediaThumb key={m.file_path} name={m.original_filename} src={m.content_type?.startsWith("image/") ? fileUrl(m.file_path) : null}
                    onRemove={() => setExistingMedia((prev) => prev.filter((x) => x.file_path !== m.file_path))} testId={`remove-existing-${m.file_path}`} />
                ))}
                {newFiles.map((f, i) => (
                  <MediaThumb key={i} name={f.name} src={f.type.startsWith("image/") ? URL.createObjectURL(f) : null}
                    onRemove={() => setNewFiles((prev) => prev.filter((_, idx) => idx !== i))} testId={`remove-new-file-${i}`} />
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="social-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="social-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : isEdit ? "Save Changes" : "Add Post"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MediaThumb({ name, src, onRemove, testId }) {
  return (
    <div className="relative group border border-[#E2E8F0] rounded-md h-16 flex items-center justify-center overflow-hidden bg-[#F8F9FA]">
      {src ? <img src={src} alt={name} className="h-full w-full object-cover" /> : <FileText className="h-5 w-5 text-[#4B5563]" />}
      <button type="button" data-testid={testId} onClick={onRemove} className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-black/60 text-white flex items-center justify-center">
        <X className="h-2.5 w-2.5" />
      </button>
    </div>
  );
}
